// Minimal client for the Cognito user-level APIs (MFA enrollment, profile
// attributes, password change), called directly from the browser with the
// user's access token — the OIDC scope includes aws.cognito.signin.user.admin,
// which authorizes these operations. Plain fetch with X-Amz-Target headers,
// matching the app's no-SDK style (api.ts).

const COGNITO_ENDPOINT = "https://cognito-idp.us-east-1.amazonaws.com/"

async function cognitoCall<T>(target: string, body: object): Promise<T> {
  const response = await fetch(COGNITO_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  })
  const json = await response.json().catch(() => ({}))
  if (!response.ok) {
    // Cognito errors carry the exception class in __type,
    // e.g. "CodeMismatchException" or "com.amazonaws...#CodeMismatchException"
    const type = String(json.__type ?? "").split("#").pop() ?? ""
    const error = new Error(json.message ?? json.Message ?? type ?? `Cognito error (HTTP ${response.status})`)
    error.name = type || "CognitoError"
    throw error
  }
  return json as T
}

export function associateSoftwareToken(accessToken: string): Promise<{ SecretCode: string }> {
  return cognitoCall("AssociateSoftwareToken", { AccessToken: accessToken })
}

export function verifySoftwareToken(accessToken: string, userCode: string): Promise<{ Status: "SUCCESS" | "ERROR" }> {
  return cognitoCall("VerifySoftwareToken", { AccessToken: accessToken, UserCode: userCode })
}

// Enabled + PreferredMfa makes Cognito insert the TOTP challenge into every
// hosted-UI login for this user.
export function enableSoftwareTokenMfa(accessToken: string): Promise<object> {
  return cognitoCall("SetUserMFAPreference", {
    AccessToken: accessToken,
    SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true },
  })
}

// Email OTP needs no associate/verify step — the account email is already
// verified. Requires the user pool to be on the Essentials feature plan or
// higher.
export function enableEmailMfa(accessToken: string): Promise<object> {
  return cognitoCall("SetUserMFAPreference", {
    AccessToken: accessToken,
    EmailMfaSettings: { Enabled: true, PreferredMfa: true },
  })
}

// Writes standard user attributes (given_name, family_name, picture, …).
// The app client must have write permission for each attribute or Cognito
// answers NotAuthorizedException.
export function updateUserAttributes(accessToken: string, attributes: { Name: string, Value: string }[]): Promise<object> {
  return cognitoCall("UpdateUserAttributes", {
    AccessToken: accessToken,
    UserAttributes: attributes,
  })
}

export function changePassword(accessToken: string, previousPassword: string, proposedPassword: string): Promise<object> {
  return cognitoCall("ChangePassword", {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  })
}

// SMS enrollment is a three-step flow: set the phone number, verify it with
// the code Cognito texts, then enable SMS as the preferred MFA method.
export function updatePhoneNumber(accessToken: string, phoneNumber: string): Promise<object> {
  return updateUserAttributes(accessToken, [{ Name: "phone_number", Value: phoneNumber }])
}

export function requestPhoneVerificationCode(accessToken: string): Promise<object> {
  return cognitoCall("GetUserAttributeVerificationCode", {
    AccessToken: accessToken,
    AttributeName: "phone_number",
  })
}

export function verifyPhoneNumber(accessToken: string, code: string): Promise<object> {
  return cognitoCall("VerifyUserAttribute", {
    AccessToken: accessToken,
    AttributeName: "phone_number",
    Code: code,
  })
}

export function enableSmsMfa(accessToken: string): Promise<object> {
  return cognitoCall("SetUserMFAPreference", {
    AccessToken: accessToken,
    SMSMfaSettings: { Enabled: true, PreferredMfa: true },
  })
}

// Revokes every refresh token for the user. Sessions created before MFA was
// enabled would otherwise keep refreshing without an MFA challenge for up to
// 30 days.
export function globalSignOut(accessToken: string): Promise<object> {
  return cognitoCall("GlobalSignOut", { AccessToken: accessToken })
}
